import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, FileText, CheckCircle, ArrowRight, BookOpen, CreditCard, Download, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AppelDeFondsModele = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Modèle Appel de Fonds Copropriété 2025 | Téléchargement gratuit</title>
        <meta name="description" content="Modèle gratuit d'appel de fonds pour votre copropriété. Comment rédiger et envoyer un appel de charges conforme. Téléchargement PDF et Word." />
        <meta name="keywords" content="appel de fonds copropriété, modèle appel de fonds, appel de charges copropriété, modèle lettre charges, provisions charges copropriété" />
        <link rel="canonical" href="https://copropriete.app/appel-de-fonds-modele" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Se connecter</Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">C'est gratuit</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Download className="w-4 h-4" />
              Modèle gratuit
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">Modèle d'Appel de Fonds</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Téléchargez notre modèle gratuit d'appel de fonds pour votre copropriété et découvrez comment le personnaliser.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <main className="prose prose-slate prose-lg max-w-none">
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-teal-600" />
              Qu'est-ce qu'un appel de fonds ?
            </h2>
            <p>
              L'<strong>appel de fonds</strong> (ou appel de charges) est le document par lequel le syndic demande aux copropriétaires le paiement de leur quote-part de charges. Il est envoyé généralement chaque trimestre pour collecter les provisions sur charges.
            </p>
            <p>
              Un appel de fonds doit contenir certaines <strong>mentions obligatoires</strong> pour être conforme :
            </p>
            <ul>
              <li>Identification du syndicat des copropriétaires</li>
              <li>Identification du copropriétaire</li>
              <li>Période concernée</li>
              <li>Montant appelé avec détail par nature de charges</li>
              <li>Date d'exigibilité</li>
              <li>Modalités de paiement</li>
            </ul>
          </section>

          {/* Modèle */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-teal-600" />
              Modèle d'appel de fonds
            </h2>
            
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 font-mono text-sm">
              <div className="text-center mb-8">
                <p className="font-bold text-lg">SYNDICAT DES COPROPRIÉTAIRES</p>
                <p>[Nom de la résidence]</p>
                <p>[Adresse de l'immeuble]</p>
              </div>
              
              <div className="mb-6">
                <p>[Nom du copropriétaire]</p>
                <p>[Adresse du lot]</p>
                <p>Lot n° [XX] - Tantièmes : [XXX]/1000</p>
              </div>
              
              <div className="text-right mb-6">
                <p>[Lieu], le [Date]</p>
              </div>
              
              <p className="font-bold mb-4">Objet : Appel de provisions sur charges - [Trimestre] [Année]</p>
              
              <p className="mb-4">Madame, Monsieur,</p>
              
              <p className="mb-4">
                Conformément au budget prévisionnel voté lors de l'assemblée générale du [date], 
                je vous prie de bien vouloir trouver ci-dessous le détail de votre appel de provisions 
                pour le [Xème] trimestre [année] :
              </p>
              
              <div className="bg-white p-4 rounded border mb-4">
                <table className="w-full">
                  <tbody>
                    <tr><td>Charges générales</td><td className="text-right">[XXX,XX] €</td></tr>
                    <tr><td>Charges ascenseur</td><td className="text-right">[XXX,XX] €</td></tr>
                    <tr><td>Charges chauffage</td><td className="text-right">[XXX,XX] €</td></tr>
                    <tr className="border-t font-bold"><td>TOTAL À PAYER</td><td className="text-right">[XXX,XX] €</td></tr>
                  </tbody>
                </table>
              </div>
              
              <p className="mb-4">
                <strong>Date d'exigibilité :</strong> [1er jour du trimestre]
              </p>
              
              <p className="mb-4">
                <strong>Modalités de paiement :</strong><br />
                - Par virement bancaire : IBAN [XXXX XXXX XXXX XXXX]<br />
                - Par chèque à l'ordre de : "Syndicat des copropriétaires [Nom résidence]"
              </p>
              
              <p className="mb-4">
                En cas de retard de paiement, des pénalités pourront être appliquées conformément 
                aux décisions de l'assemblée générale.
              </p>
              
              <p className="mb-4">
                Je reste à votre disposition pour tout renseignement complémentaire.
              </p>
              
              <p>Veuillez agréer, Madame, Monsieur, l'expression de mes salutations distinguées.</p>
              
              <div className="mt-8">
                <p>Le Syndic</p>
                <p>[Signature]</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Mail className="w-8 h-8 text-teal-600" />
              Envoi de l'appel de fonds
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Quand envoyer ?</h3>
            <p>
              L'appel de fonds doit être envoyé <strong>avant la date d'exigibilité</strong>, généralement 15 jours avant le début du trimestre concerné.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Comment envoyer ?</h3>
            <ul>
              <li><strong>Courrier simple</strong> - Mode d'envoi classique</li>
              <li><strong>Email</strong> - Si le copropriétaire a donné son accord</li>
              <li><strong>Plateforme en ligne</strong> - Solution moderne et économique</li>
            </ul>

            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl my-8">
              <p className="text-emerald-900 font-medium m-0">
                ✅ <strong>Avec copropriete.app</strong>, générez et envoyez automatiquement vos appels de fonds à tous vos copropriétaires en un clic !
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Calcul de l'appel de fonds</h2>
            
            <p>La formule de calcul est simple :</p>
            
            <div className="bg-teal-50 rounded-xl p-6 my-6 text-center">
              <p className="font-mono text-lg font-bold">
                Montant = (Budget annuel × Tantièmes du lot / 1000) ÷ 4
              </p>
            </div>

            <h3 className="text-xl font-bold text-slate-800">Exemple concret</h3>
            <ul>
              <li>Budget prévisionnel annuel : 40 000 €</li>
              <li>Tantièmes du lot : 85/1000</li>
              <li>Charges annuelles du lot : 40 000 × 85/1000 = 3 400 €</li>
              <li><strong>Appel trimestriel : 3 400 ÷ 4 = 850 €</strong></li>
            </ul>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 my-8 border border-teal-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Automatisez vos appels de fonds</h3>
            <p className="text-slate-700 mb-6">
              Plus besoin de calculer manuellement ! copropriete.app génère automatiquement vos appels de fonds selon les tantièmes de chaque copropriétaire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-teal-600 hover:bg-teal-700">
                Essayer gratuitement pendant 7 jours
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default AppelDeFondsModele;
